import React, { useCallback, useState } from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import type { MetricAutoAddRuleSource } from "@/metrics/runtime-adapters"
import {
  Button,
  Card,
  CardContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectionChip,
  Switch,
  useHoverOpen,
} from "@coval/ui"

import {
  type MetricValueFilter as MetricFilter,
  type MetricValueFilterOperator as MetricFilterOperator,
} from "@/lib/metricValueFilters"
import { FilterMenuButton } from "@/components/filters/FilterMenuButton"
import {
  SearchableCheckboxList,
  type SearchableItem,
} from "@/components/filters/SearchableCheckboxList"
import type { RunRowLike } from "@/types/RunRow"
import {
  formatMetricDisplayName,
  resolveMetricDisplayName,
} from "@/utils/formatting/format-metric-display-results"

import { useRunFilterContext } from "../contexts/RunFilterContext"
import {
  MetricsSection,
  MutationsFilterSection,
  StatusFilterSection,
} from "./RunFilterSections"

export {
  getDefaultFilterValue,
  getMetricFilterInfo,
  resolveMetricFilterInfo,
} from "./RunFilterSections"

interface RunFilterDropdownProps {
  /** When provided, the dropdown open state is controlled by the parent. */
  isOpen?: boolean
  /** When provided with isOpen, called when the dropdown open state changes. */
  onOpenChange?: (open: boolean) => void
  /** Mutation id -> display name (from run rows). */
  mutationNameById: Map<string, string>
  /** Mutation filter: selected mutation IDs (IDs under the hood). */
  mutationFilterIds: string[]
  onMutationFilterChange: (ids: string[]) => void
  /** Status filter: selected statuses (e.g. COMPLETED, FAILED). */
  statusFilterStatuses: string[]
  onStatusFilterChange: (statuses: string[]) => void
  isOutliersEnabled?: boolean
  onOutliersEnabledChange?: (enabled: boolean) => void
  outlierMetricIds?: string[] | null
  outlierMetricOptions?: SearchableItem[]
  onOutlierMetricIdsChange?: (ids: string[] | null) => void
  /** Metric filter state and handlers (for Metrics section, same as MetricFilterDropdown). */
  metricRecordsById: Record<string, MetricIdentity>
  /** Richer per-metric source carrying categorical options + metadata for value pre-fill. */
  metricSourcesById: Record<string, MetricAutoAddRuleSource>
  runRows: RunRowLike[]
  metricFilters: MetricFilter[]
  onAddMetricFilter: (
    metricName: string,
    operator: MetricFilterOperator,
    value: number | string | null,
    rangeMax?: number,
  ) => void
  onRemoveMetricFilter: (index: number) => void
  onClearAllMetricFilters: () => void
}

export const RunFilterDropdown: React.FC<RunFilterDropdownProps> = ({
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  mutationNameById,
  mutationFilterIds,
  onMutationFilterChange,
  statusFilterStatuses,
  onStatusFilterChange,
  isOutliersEnabled = false,
  onOutliersEnabledChange,
  outlierMetricIds = null,
  outlierMetricOptions = [],
  onOutlierMetricIdsChange,
  metricRecordsById,
  metricSourcesById,
  runRows,
  metricFilters,
  onAddMetricFilter,
  onRemoveMetricFilter,
  onClearAllMetricFilters,
}) => {
  const { totalActiveFilters: contextFilterCount } = useRunFilterContext()
  const [openSection, setOpenSection] = useState<
    "mutations" | "metrics" | "outliers" | "status" | null
  >(null)

  // The count reflects mutation + metric + status filters
  const totalActiveFilters =
    contextFilterCount +
    mutationFilterIds.length +
    metricFilters.length +
    statusFilterStatuses.length +
    Number(isOutliersEnabled)

  const isControlled = controlledOnOpenChange !== undefined
  const mainPopoverOpen = isControlled ? (controlledIsOpen ?? false) : undefined
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setOpenSection(null)
      }
      if (isControlled) {
        controlledOnOpenChange?.(open)
      }
    },
    [isControlled, controlledOnOpenChange],
  )

  return (
    <Popover
      {...(isControlled ? { open: mainPopoverOpen } : {})}
      onOpenChange={onOpenChange}
    >
      <PopoverTrigger asChild>
        <FilterMenuButton
          activeCount={totalActiveFilters}
          label="Filter results"
          onClick={() => {
            if (isControlled) {
              controlledOnOpenChange?.(!mainPopoverOpen)
            }
          }}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-0">
        <Card className="border-0 shadow-none">
          <CardContent className="flex flex-col gap-1 p-2">
            <MutationsFilterSection
              isOpen={openSection === "mutations"}
              onOpenChange={(open) => setOpenSection(open ? "mutations" : null)}
              selectedMutationIds={mutationFilterIds}
              onMutationFilterChange={onMutationFilterChange}
              mutationNameById={mutationNameById}
            />
            <MetricsSection
              isOpen={openSection === "metrics"}
              onOpenChange={(open) => setOpenSection(open ? "metrics" : null)}
              metricRecordsById={metricRecordsById}
              metricSourcesById={metricSourcesById}
              runRows={runRows}
              filters={metricFilters}
              onAddFilter={onAddMetricFilter}
              onRemoveFilter={onRemoveMetricFilter}
              onClearAllFilters={onClearAllMetricFilters}
            />
            <StatusFilterSection
              isOpen={openSection === "status"}
              onOpenChange={(open) => setOpenSection(open ? "status" : null)}
              selectedStatuses={statusFilterStatuses}
              onStatusFilterChange={onStatusFilterChange}
            />
            {onOutliersEnabledChange && (
              <>
                <div className="flex items-center justify-between px-2 py-1.5">
                  <label htmlFor="run-outliers-toggle" className="text-sm">
                    Outliers
                  </label>
                  <Switch
                    id="run-outliers-toggle"
                    aria-label="Highlight outliers"
                    checked={isOutliersEnabled}
                    onCheckedChange={onOutliersEnabledChange}
                  />
                </div>
                {isOutliersEnabled && onOutlierMetricIdsChange && (
                  <OutlierMetricsSection
                    isOpen={openSection === "outliers"}
                    onOpenChange={(open) =>
                      setOpenSection(open ? "outliers" : null)
                    }
                    metricOptions={outlierMetricOptions}
                    selectedMetricIds={outlierMetricIds}
                    onSelectionChange={onOutlierMetricIdsChange}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  )
}

export function OutlierMetricsSection({
  isOpen,
  metricOptions,
  onOpenChange,
  onSelectionChange,
  selectedMetricIds,
}: {
  isOpen: boolean
  metricOptions: SearchableItem[]
  onOpenChange: (open: boolean) => void
  onSelectionChange: (ids: string[] | null) => void
  selectedMetricIds: string[] | null
}) {
  const hoverProps = useHoverOpen({ open: isOpen, onOpenChange })
  const selectedIds =
    selectedMetricIds ?? metricOptions.map((metric) => metric.id)
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          {...hoverProps}
          className="flex w-full items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-surface-1"
        >
          <span>Outlier metrics</span>
          <span className="text-coval-text-muted">
            {selectedIds.length}/{metricOptions.length}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={15}
        className="w-80 p-0"
      >
        <div className="flex items-center justify-between border-b border-coval-border px-3 py-2">
          <span className="text-sm font-medium">Outlier metrics</span>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              aria-label="Select all outlier metrics"
              onClick={() => onSelectionChange(null)}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="ghost"
              aria-label="Clear outlier metrics"
              onClick={() => onSelectionChange([])}
            >
              Clear
            </Button>
          </div>
        </div>
        <SearchableCheckboxList
          items={metricOptions}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          isLoading={false}
          isOpen={isOpen}
          placeholder="Search outlier metrics..."
          emptyMessage="No outlier metrics found"
          noItemsMessage="No metric samples available"
        />
      </PopoverContent>
    </Popover>
  )
}

/** Combined filter chips for run results: mutations + metrics + status, with one "X Clear" for all. */
export const RunResultsFilterChips: React.FC<{
  mutationFilterIds: string[]
  mutationNameById: Map<string, string>
  onRemoveMutation: (mutationId: string) => void
  statusFilterStatuses: string[]
  onRemoveStatus: (status: string) => void
  metricFilters: MetricFilter[]
  metricRecordsById: Record<string, MetricIdentity>
  onRemoveMetricFilter: (index: number) => void
  onClearAllFilters: () => void
}> = ({
  mutationFilterIds,
  mutationNameById,
  onRemoveMutation,
  statusFilterStatuses,
  onRemoveStatus,
  metricFilters,
  metricRecordsById,
  onRemoveMetricFilter,
  onClearAllFilters,
}) => {
  const hasAnyFilters =
    mutationFilterIds.length > 0 ||
    metricFilters.length > 0 ||
    statusFilterStatuses.length > 0
  if (!hasAnyFilters) {
    return null
  }

  return (
    <div className="flex gap-2 flex-nowrap items-center min-w-0 flex-1 overflow-hidden">
      <div
        className="flex min-w-0 shrink flex-nowrap items-center gap-1.5 overflow-x-auto"
        style={{ scrollbarWidth: "none" }}
      >
        {mutationFilterIds.map((id) => (
          <SelectionChip
            key={id}
            label={mutationNameById.get(id) ?? id}
            className="max-w-[150px] cursor-default select-none"
            removeLabel={`Remove ${mutationNameById.get(id) ?? id} filter`}
            onRemove={() => onRemoveMutation(id)}
          />
        ))}
        {statusFilterStatuses.map((status) => (
          <SelectionChip
            key={status}
            label={status.toLowerCase()}
            className="max-w-[150px] capitalize cursor-default select-none"
            removeLabel={`Remove ${status.toLowerCase()} filter`}
            onRemove={() => onRemoveStatus(status)}
          />
        ))}
        {metricFilters.map((filter, index) => {
          const label = `${resolveMetricDisplayName(
            metricRecordsById[filter.metricName]?.metric_display_name,
            filter.metricName,
          )}: ${
            filter.operator === "exists"
              ? "exists"
              : filter.operator === "range"
                ? `${filter.value} – ${filter.rangeMax}`
                : filter.valueType === "string" &&
                    typeof filter.value === "string"
                  ? `${filter.operator} ${formatMetricDisplayName(filter.value)}`
                  : `${filter.operator} ${String(filter.value).toLowerCase()}`
          }`
          return (
            <SelectionChip
              key={`metric-${index}`}
              label={label}
              className="max-w-[150px] cursor-default select-none"
              removeLabel={`Remove ${label} filter`}
              onRemove={() => onRemoveMetricFilter(index)}
            />
          )
        })}
      </div>
      <Button variant="ghost" className="shrink-0" onClick={onClearAllFilters}>
        Clear All
      </Button>
    </div>
  )
}
